// React
import { useEffect, useMemo, useState } from "react";
// React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// MUI
import { Stack, Typography } from "@mui/material";

// API
import { getUsers } from "@Common/api/users";
import { updateUser } from "@/User/api/UserPreferences/mutations";
import { onUpdateUserSubscription } from "@Settings/api/AdministrationTools/subscriptions";

// Hooks
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Components
import InfoSubTitle from "@Settings/components/SystemSettings/common/InfoSubTitle";
import SmartSelector from "@Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@Layouts/components/InputComponents/SimpleTextField";
import Title from "@Settings/components/SystemSettings/common/Title";

// Types
import { Nullable } from "@Common/interfaces/types";
import { IUser } from "@Common/interfaces/user";
import { TSelectValue } from "@Layouts/components/InputComponents/SmartSelector/interfaces";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";

type TKMSUserEditProps = Readonly<{
	locationId: string | undefined;
}>;

type TUserWithModifiedName = IUser & { bundledName: string };

export default function KMSUserEdit(props: TKMSUserEditProps) {
	const queryClient = useQueryClient();

	const { addSubscription, deleteSubscription, getSubscription } = useSubscriptions();

	const [selectedUser, setSelectedUser] = useState<IUser | null>(null);

	/********************************************* QUERIES ************************************************/
	const { data: allUsers, isLoading } = useQuery({
		queryKey: ["AllUsersForKMSEditArea", props.locationId],
		queryFn: () => getUsers(props.locationId as string),
		enabled: Boolean(props.locationId),
		staleTime: Infinity,
	});

	/********************************************* MUTATIONS ************************************************/
	const { mutate: updateKMSIdMutation, isPending } = useMutation({
		mutationKey: ["updateKMSUser", props.locationId, selectedUser?._id],
		mutationFn: (updatedUser: IUser) => updateUser(selectedUser?._id, props.locationId, updatedUser),
	});

	/********************************************* SUBSCRIPTIONS ************************************************/
	const kmsUserEditSubscriptions = getSubscription(["KMSUserEditArea", props.locationId]);
	kmsUserEditSubscriptions?.subscriptions?.onUpdateUser?.setCallback((response: GraphqlSubscriptionResponse<"onUpdateUser", IUser>) => {
		const updatedUser = response.data?.onUpdateUser;

		queryClient.setQueryData(["AllUsersForKMSEditArea", props.locationId], (cachedData: IUser[]) => {
			if (!updatedUser) {
				return cachedData;
			}

			if (updatedUser._id === selectedUser?._id) {
				setSelectedUser((prevSelectedUser) => ({ ...prevSelectedUser, kmsUserId: updatedUser.kmsUserId }));
			}

			return cachedData.map((user) => {
				return user._id === updatedUser._id ? { ...user, kmsUserId: updatedUser.kmsUserId } : user;
			});
		});
	});

	/********************************************* HANDLERS ************************************************/
	function handleUserChange(updatedUser: TSelectValue): void {
		setSelectedUser(updatedUser as IUser);
	}

	function handleKMSIdChange(userToUpdate: Nullable<TUserWithModifiedName>, updatedKMSId: string): void {
		if (!userToUpdate) {
			return;
		}

		if (userToUpdate?.kmsUserId === updatedKMSId) {
			return;
		}

		const { supervisor, communications, bundledName, ...userWithoutSupervisor } = userToUpdate;
		const updatedUser = { ...userWithoutSupervisor, kmsUserId: updatedKMSId };
		updateKMSIdMutation(updatedUser);
	}

	const usersWithModifiedName = useMemo(() => {
		if (!allUsers?.length) {
			return [];
		}

		return allUsers.map((user) => {
			return {
				...user,
				bundledName: `${user.fullName} (${user.employeeId})`,
			};
		});
	}, [allUsers]);

	useEffect(() => {
		if (props.locationId) {
			addSubscription(
				["KMSUserEditArea", props.locationId],
				[
					{
						context: "onUpdateUser",
						query: onUpdateUserSubscription,
						variables: {
							locationId: props.locationId,
						},
					},
				]
			);
		}
		return () => {
			deleteSubscription(["KMSUserEditArea", props.locationId]);
		};
	}, [props.locationId, addSubscription]);

	return (
		<Stack>
			<Title title={"Key Management System User ID Edit"} className={"!mb-4"} />
			<Stack direction={"row"} gap={1}>
				<Stack spacing={1.5}>
					<InfoSubTitle title={"User"} />
					<SmartSelector
						options={usersWithModifiedName ?? []}
						selectBy={"_id"}
						showBy={"bundledName"}
						sortBy={"fullName"}
						loading={isPending || isLoading}
						disabled={isPending || isLoading}
						classes="!w-[240px]"
						inputProps={{
							placeholder: "Select a user",
						}}
						// Callbacks
						onChangeAction={handleUserChange}
					/>
				</Stack>
				<Stack alignContent={"flex-end"} alignSelf={"flex-end"} pb={0.5}>
					<Typography component={"span"} className={"!text-disabled"}>
						-
					</Typography>
				</Stack>
				<Stack spacing={1.5}>
					<InfoSubTitle title={"ID"} />
					<SimpleTextField
						outlined
						editable
						text={selectedUser?.kmsUserId ?? ""}
						classes="!w-[240px]"
						placeholder="Kms ID"
						disabled={isPending || !selectedUser}
						isValid={(value) => /^[A-Za-z0-9]*$/.test(value)}
						// Callbacks
						onActionUpdate={(kmsUserId) => handleKMSIdChange(selectedUser as Nullable<TUserWithModifiedName>, kmsUserId)}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
}
